import { CellContext, ColumnDef } from "@tanstack/react-table";
import { TrashAltIcon } from "assets/icons";
import { t } from "i18next";
import { StaffManagerPermissions, useProfile } from "modules/common";
import moment from "moment";
import { useMemo } from "react";
import { Button, IconButton, ItemsTable } from "ui";

import { EmailNotificationRecipient } from "../../emailNotifications.types";
import { EventTypeBadge } from "../EventTypeBadge";

import styles from "./RecipientsTable.module.scss";

interface RecipientsTableProps {
  recipients: EmailNotificationRecipient[];
  isLoading?: boolean;
  onRemove: (recipient: EmailNotificationRecipient) => void;
  onReactivate: (recipient: EmailNotificationRecipient) => void;
}

const RecipientsTable = ({
  recipients,
  isLoading,
  onRemove,
  onReactivate
}: RecipientsTableProps) => {
  const { user } = useProfile({ enabled: false });
  const { canManageRecipients } = useMemo(() => {
    return {
      canManageRecipients:
        user.role === "Super" ||
        user.permissions?.includes(
          StaffManagerPermissions.ManageEmailNotificationRecipients
        )
    };
  }, [user]);

  const columns: Array<ColumnDef<EmailNotificationRecipient, string>> = useMemo(
    () => [
      {
        id: "email",
        accessorKey: "email",
        header: t("Email"),
        size: 28,
        minSize: 28,
        enableSorting: false,
        cell: ({ row }) => (
          <span className={styles.email}>{recipients[row.index].email}</span>
        )
      },
      {
        id: "recipient",
        accessorKey: "adminName",
        header: t("Recipient"),
        size: 22,
        minSize: 22,
        enableSorting: false,
        cell: ({ row }) => {
          const { adminName } = recipients[row.index];

          return adminName ? (
            <span>{adminName}</span>
          ) : (
            <span className={styles.external}>{t("External")}</span>
          );
        }
      },
      {
        id: "eventType",
        accessorKey: "eventType",
        header: t("Event type"),
        size: 18,
        minSize: 18,
        enableSorting: false,
        cell: ({ row }) => (
          <EventTypeBadge eventType={recipients[row.index].eventType} />
        )
      },
      {
        id: "status",
        accessorKey: "isActive",
        header: t("Status"),
        size: 12,
        minSize: 12,
        enableSorting: false,
        cell: ({ row }) =>
          recipients[row.index].isActive ? (
            <span className={styles.active}>{t("Active")}</span>
          ) : (
            <span className={styles.inactive}>{t("Inactive")}</span>
          )
      },
      {
        id: "createdAt",
        accessorKey: "createdAt",
        header: t("Added"),
        size: 12,
        minSize: 12,
        enableSorting: false,
        cell: ({ row }) => (
          <span className={styles.nowrap}>
            {moment(recipients[row.index].createdAt).format("DD/MM/YYYY")}
          </span>
        )
      },
      ...(canManageRecipients
        ? [
            {
              id: "actions",
              accessorKey: "id",
              header: "",
              size: 8,
              minSize: 8,
              enableSorting: false,
              cell: ({
                row
              }: CellContext<EmailNotificationRecipient, string>) => {
                const recipient = recipients[row.index];

                return recipient.isActive ? (
                  <IconButton
                    icon={
                      <TrashAltIcon fill="#ff0033" width={20} height={20} />
                    }
                    onClick={() => onRemove(recipient)}
                  />
                ) : (
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => onReactivate(recipient)}
                  >
                    {t("Reactivate")}
                  </Button>
                );
              }
            }
          ]
        : [])
    ],
    [recipients, user]
  );

  const inactiveRowsIndexes = useMemo(
    () =>
      recipients.reduce<number[]>((acc, recipient, index) => {
        if (!recipient.isActive) acc.push(index);
        return acc;
      }, []),
    [recipients]
  );

  return (
    <ItemsTable
      columns={columns}
      data={recipients}
      isLoading={isLoading}
      skeletonHeight={56}
      emptyListMessage={t("No recipients yet")}
      rowsClassName={styles.tallRow}
      specificRowsIndexes={inactiveRowsIndexes}
      specificRowsClassName={styles.inactiveRow}
    />
  );
};

export default RecipientsTable;
